import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Lightbulb,
  Sparkles,
  Bug,
  Inbox,
  Eye,
  FileText,
  CheckCircle,
  ArrowRightCircle,
  XCircle,
  Archive,
  Loader2,
  Calendar,
  User,
  Tag,
  X,
  Plus,
  Send,
  MessageSquare,
  ExternalLink,
  ThumbsUp,
  Edit,
  Reply,
  CornerDownRight,
  Info,
} from 'lucide-react';
import { api } from '../../lib/api';
import { formatDate, formatDateTime } from '../../lib/utils';
import { HtmlRenderer } from '../../components/editor/HtmlRenderer';
import { RichTextEditor } from '../../components/editor/RichTextEditor';
import { useAuthStore } from '../../stores/authStore';
import { useUIStore } from '../../stores/uiStore';

interface RequestDetail {
  id: string;
  projectId: string;
  projectName: string;
  title: string;
  descriptionHtml: string | null;
  type: 'idea' | 'feature' | 'bug';
  status: string;
  priority: string | null;
  createdBy: string;
  createdByName: string;
  reviewedBy: string | null;
  reviewedByName: string | null;
  approvedBy: string | null;
  approvedByName: string | null;
  approvedAt: string | null;
  convertedToWorkItemId: string | null;
  mergedIntoRequestId: string | null;
  createdAt: string;
  updatedAt: string;
  tags: Array<{ id: string; name: string; color: string }>;
  comments: Array<{
    id: string;
    userId: string;
    userName: string;
    content: string | null;
    contentHtml: string | null;
    isInternal: boolean;
    parentCommentId: string | null;
    createdAt: string;
  }>;
}

interface TagOption {
  id: string;
  name: string;
  color: string;
}

const typeConfig = {
  idea: { icon: Lightbulb, label: 'Idea', color: 'text-amber-500', bg: 'bg-amber-500/10' },
  feature: { icon: Sparkles, label: 'Feature', color: 'text-violet-500', bg: 'bg-violet-500/10' },
  bug: { icon: Bug, label: 'Bug', color: 'text-red-500', bg: 'bg-red-500/10' },
};

const statusConfig: Record<string, { icon: typeof Inbox; label: string; color: string; bg: string }> = {
  new: { icon: Inbox, label: 'New', color: 'text-blue-500', bg: 'bg-blue-500/10' },
  under_review: { icon: Eye, label: 'Under Review', color: 'text-amber-500', bg: 'bg-amber-500/10' },
  plan_proposed: { icon: FileText, label: 'Plan Proposed', color: 'text-violet-500', bg: 'bg-violet-500/10' },
  approved: { icon: CheckCircle, label: 'Approved', color: 'text-green-500', bg: 'bg-green-500/10' },
  converted: { icon: ArrowRightCircle, label: 'Converted', color: 'text-cyan-500', bg: 'bg-cyan-500/10' },
  declined: { icon: XCircle, label: 'Declined', color: 'text-red-500', bg: 'bg-red-500/10' },
  archived: { icon: Archive, label: 'Archived', color: 'text-gray-500', bg: 'bg-gray-500/10' },
};

export function RequestDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const { setSidebarCollapsed } = useUIStore();

  const [request, setRequest] = useState<RequestDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [availableTags, setAvailableTags] = useState<TagOption[]>([]);
  const [showTagDropdown, setShowTagDropdown] = useState(false);

  const [newComment, setNewComment] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [replyingTo, setReplyingTo] = useState<{ id: string; userName: string; content: string } | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [showCommentForm, setShowCommentForm] = useState(false);

  const [isApproving, setIsApproving] = useState(false);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ title: '', descriptionHtml: '' });
  const [isSaving, setIsSaving] = useState(false);

  // Collapse sidebar when editing
  useEffect(() => {
    if (isEditing) {
      setSidebarCollapsed(true);
    } else {
      setSidebarCollapsed(false);
    }
    return () => setSidebarCollapsed(false);
  }, [isEditing, setSidebarCollapsed]);

  const fetchRequest = useCallback(async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      const response = await api.get<RequestDetail>(`/requests/${id}`);
      setRequest(response.data);
      setEditForm({
        title: response.data.title,
        descriptionHtml: response.data.descriptionHtml || '',
      });
      setError(null);
    } catch (err) {
      setError('Failed to load request');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchRequest();
  }, [fetchRequest]);

  useEffect(() => {
    async function fetchTags() {
      try {
        const response = await api.get<TagOption[]>('/tags');
        setAvailableTags(response.data || []);
      } catch (err) {
        console.error('Failed to load tags:', err);
      }
    }
    fetchTags();
  }, []);

  const handleAddTag = async (tagId: string) => {
    if (!request) return;
    try {
      await api.post(`/requests/${request.id}/tags`, { tagId });
      await fetchRequest();
      setShowTagDropdown(false);
    } catch (err) {
      console.error('Failed to add tag:', err);
    }
  };

  const handleRemoveTag = async (tagId: string) => {
    if (!request) return;
    try {
      await api.delete(`/requests/${request.id}/tags/${tagId}`);
      await fetchRequest();
    } catch (err) {
      console.error('Failed to remove tag:', err);
    }
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    // Strip HTML tags to check if there's actual content
    const plainText = newComment.replace(/<[^>]*>/g, '').trim();
    if (!request || !plainText) return;

    try {
      setIsSubmittingComment(true);
      await api.post(`/requests/${request.id}/comments`, {
        content: plainText,
        contentHtml: newComment,
      });
      setNewComment('');
      await fetchRequest();
    } catch (err) {
      console.error('Failed to submit comment:', err);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleSubmitReply = async (e: React.FormEvent) => {
    e.preventDefault();
    const plainText = replyContent.replace(/<[^>]*>/g, '').trim();
    if (!request || !replyingTo || !plainText) return;

    try {
      setIsSubmittingComment(true);
      await api.post(`/requests/${request.id}/comments`, {
        content: plainText,
        contentHtml: replyContent,
        parentCommentId: replyingTo.id,
      });
      setReplyContent('');
      setReplyingTo(null);
      await fetchRequest();
    } catch (err) {
      console.error('Failed to submit reply:', err);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const startReply = (comment: { id: string; userName: string; content: string | null; contentHtml: string | null }) => {
    // Get plain text preview from HTML or content
    const preview = comment.contentHtml
      ? comment.contentHtml.replace(/<[^>]*>/g, '').substring(0, 100)
      : (comment.content || '').substring(0, 100);
    setReplyingTo({ id: comment.id, userName: comment.userName, content: preview });
    setReplyContent('');
  };

  const handleApprovePlan = async () => {
    if (!request) return;
    try {
      setIsApproving(true);
      await api.post(`/requests/${request.id}/approve`);
      await fetchRequest();
    } catch (err) {
      console.error('Failed to approve plan:', err);
    } finally {
      setIsApproving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!request) return;
    try {
      setIsSaving(true);
      await api.patch(`/requests/${request.id}`, {
        title: editForm.title,
        descriptionHtml: editForm.descriptionHtml,
      });
      setRequest({ ...request, title: editForm.title, descriptionHtml: editForm.descriptionHtml });
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to save:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const startEditing = () => {
    setEditForm({
      title: request?.title || '',
      descriptionHtml: request?.descriptionHtml || '',
    });
    setIsEditing(true);
  };

  // Check if user can edit (creator and status is new or under_review)
  const canEdit = request && user && request.createdBy === user.id &&
    (request.status === 'new' || request.status === 'under_review');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  if (error || !request) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">{error || 'Request not found'}</p>
        <Link to="/requests" className="text-accent hover:underline">
          Back to Requests
        </Link>
      </div>
    );
  }

  const type = typeConfig[request.type];
  const status = statusConfig[request.status];
  const TypeIcon = type?.icon || Lightbulb;
  const StatusIcon = status?.icon || Inbox;

  const requestTags = request.tags || [];
  const unusedTags = availableTags.filter(
    (tag) => !requestTags.some((t) => t.id === tag.id)
  );

  // Filter out internal comments for client view and organize into threads
  const allVisibleComments = (request.comments || []).filter((c) => !c.isInternal);
  const topLevelComments = allVisibleComments.filter((c) => !c.parentCommentId);
  const getReplies = (parentId: string) =>
    allVisibleComments.filter((c) => c.parentCommentId === parentId);
  const visibleComments = topLevelComments; // For count display

  // Edit mode view
  if (isEditing) {
    return (
      <div className="flex-1 flex flex-col min-h-0">
        {/* Header Bar */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-4"
        >
          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsEditing(false)}
              className="p-2 rounded-xl hover:bg-bg-secondary transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-bold">Edit Request</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsEditing(false)}
              className="px-4 py-2 text-text-secondary hover:text-text transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveEdit}
              disabled={isSaving || !editForm.title.trim()}
              className="inline-flex items-center gap-2 px-4 py-2 bg-accent hover:bg-accent-dark disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : null}
              Save
            </button>
          </div>
        </motion.div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex-1 grid grid-cols-1 lg:grid-cols-6 gap-6 min-h-0"
        >
          {/* Left Column - Title + Editor (5/6) */}
          <div className="lg:col-span-5 flex flex-col min-h-0 order-2 lg:order-1">
            {/* Title */}
            <div className="mb-3">
              <input
                type="text"
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                placeholder="Request title..."
                className="w-full px-4 py-3 bg-bg-secondary border border-border rounded-xl text-text text-lg placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/50"
              />
            </div>
            {/* Editor */}
            <div className="flex-1 min-h-0">
              <RichTextEditor
                content={editForm.descriptionHtml}
                onChange={(html) => setEditForm({ ...editForm, descriptionHtml: html })}
                placeholder="Describe your request in detail... You can paste or drag images here and annotate them by clicking on them."
                className="h-full"
              />
            </div>
          </div>

          {/* Right Column - Info (1/6) */}
          <div className="lg:col-span-1 space-y-4 order-1 lg:order-2">
            {/* Type */}
            <div>
              <label className="block text-sm font-medium mb-2">Type</label>
              <div className={`flex items-center gap-2 p-2.5 rounded-xl ${type?.bg || 'bg-gray-500/10'}`}>
                <TypeIcon className={`w-4 h-4 ${type?.color || 'text-gray-500'}`} />
                <span className={`font-medium text-sm ${type?.color || 'text-gray-500'}`}>
                  {type?.label || request.type}
                </span>
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium mb-2">Status</label>
              <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${status?.bg || 'bg-gray-500/10'} ${status?.color || 'text-gray-500'}`}>
                <StatusIcon className="w-4 h-4" />
                {status?.label || request.status}
              </div>
            </div>

            {/* Project */}
            <div>
              <label className="block text-sm font-medium mb-2">Project</label>
              <p className="text-sm text-text-secondary">{request.projectName}</p>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Link
          to="/requests"
          className="inline-flex items-center gap-2 text-text-secondary hover:text-text mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Requests
        </Link>

        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-xl ${type?.bg || 'bg-gray-500/10'}`}>
            <TypeIcon className={`w-6 h-6 ${type?.color || 'text-gray-500'}`} />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">{request.title}</h1>
            <p className="text-text-secondary mt-1">
              <Link to={`/projects/${request.projectId}`} className="hover:text-accent transition-colors">
                {request.projectName}
              </Link>
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canEdit && (
              <button
                onClick={startEditing}
                className="p-2 rounded-lg hover:bg-bg-secondary transition-colors"
                title="Edit request"
              >
                <Edit className="w-4 h-4 text-text-secondary hover:text-text" />
              </button>
            )}
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${status?.bg || 'bg-gray-500/10'} ${status?.color || 'text-gray-500'}`}>
              <StatusIcon className="w-4 h-4" />
              {status?.label || request.status}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Plan Proposed Alert */}
      {request.status === 'plan_proposed' && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="p-4 bg-violet-500/10 border border-violet-500/20 rounded-xl"
        >
          <div className="flex items-start gap-3">
            <FileText className="w-5 h-5 text-violet-500 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-violet-400">Plan Ready for Review</h3>
              <p className="text-sm text-text-secondary mt-1">
                The OML Design team has proposed a plan for this request. Please review the description below and approve if you're satisfied.
              </p>
            </div>
            <button
              onClick={handleApprovePlan}
              disabled={isApproving}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg transition-colors font-medium"
            >
              {isApproving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ThumbsUp className="w-4 h-4" />
              )}
              Approve Plan
            </button>
          </div>
        </motion.div>
      )}

      {/* Converted Notice */}
      {request.convertedToWorkItemId && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="p-4 bg-cyan-500/10 border border-cyan-500/20 rounded-xl"
        >
          <div className="flex items-center gap-3">
            <ArrowRightCircle className="w-5 h-5 text-cyan-500" />
            <div className="flex-1">
              <h3 className="font-medium text-cyan-400">Converted to Work Item</h3>
              <p className="text-sm text-text-secondary">
                This request has been converted into a work item for delivery.
              </p>
            </div>
            <Link
              to={`/work-items/${request.convertedToWorkItemId}`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors font-medium"
            >
              <ExternalLink className="w-4 h-4" />
              View Work Item
            </Link>
          </div>
        </motion.div>
      )}

      {/* Planning Phase Info - show for requests not yet converted */}
      {!request.convertedToWorkItemId && request.status !== 'declined' && request.status !== 'archived' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 bg-bg-secondary/50 border border-border/50 rounded-xl"
        >
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-text-muted flex-shrink-0 mt-0.5" />
            <p className="text-sm text-text-secondary">
              This request is in the planning phase. Once our team finalises the approach and you approve the plan, it will be converted into a deliverable work item.
            </p>
          </div>
        </motion.div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="lg:col-span-2 space-y-6"
        >
          {/* Description */}
          <div className="bg-bg-secondary rounded-xl border border-border p-6">
            <h2 className="text-lg font-semibold mb-4">Description</h2>
            {request.descriptionHtml ? (
              <HtmlRenderer html={request.descriptionHtml} />
            ) : (
              <p className="text-text-muted italic">No description provided</p>
            )}
          </div>

          {/* Comments */}
          <div className="bg-bg-secondary rounded-xl border border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-text-secondary" />
                <h2 className="text-lg font-semibold">Comments</h2>
                <span className="text-sm text-text-muted">({allVisibleComments.length})</span>
              </div>
              {!showCommentForm && !replyingTo && (
                <button
                  onClick={() => setShowCommentForm(true)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-accent hover:bg-accent-dark text-white rounded-lg transition-colors font-medium"
                >
                  <Plus className="w-4 h-4" />
                  New Comment
                </button>
              )}
            </div>

            {/* Reply Form - shown when replying to a comment */}
            {replyingTo && (
              <div className="mb-6 p-4 bg-accent/5 border border-accent/20 rounded-xl">
                <div className="flex items-start gap-2 mb-3">
                  <CornerDownRight className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-text-secondary">
                      Replying to <span className="font-medium text-accent">{replyingTo.userName}</span>
                    </p>
                    <p className="text-xs text-text-muted mt-1 line-clamp-2 italic">
                      "{replyingTo.content}..."
                    </p>
                  </div>
                  <button
                    onClick={() => setReplyingTo(null)}
                    className="p-1 hover:bg-bg-tertiary rounded transition-colors"
                  >
                    <X className="w-4 h-4 text-text-muted" />
                  </button>
                </div>
                <form onSubmit={handleSubmitReply}>
                  <RichTextEditor
                    content={replyContent}
                    onChange={setReplyContent}
                    placeholder="Write your reply... Use @ to mention someone"
                    projectId={request.projectId}
                    className="min-h-[100px]"
                  />
                  <div className="flex justify-end gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => setReplyingTo(null)}
                      className="px-3 py-1.5 text-sm text-text-secondary hover:text-text transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmittingComment || !replyContent.replace(/<[^>]*>/g, '').trim()}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-accent hover:bg-accent-dark disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
                    >
                      {isSubmittingComment ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Reply className="w-3 h-3" />
                      )}
                      Reply
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Comment Form - shown when showCommentForm is true and not replying */}
            {showCommentForm && !replyingTo && (
              <div className="mb-6 p-4 bg-bg-tertiary/50 border border-border rounded-xl">
                <form onSubmit={(e) => {
                  handleSubmitComment(e);
                  setShowCommentForm(false);
                }}>
                  <RichTextEditor
                    content={newComment}
                    onChange={setNewComment}
                    placeholder="Add a comment... Use @ to mention someone"
                    projectId={request.projectId}
                    className="min-h-[100px]"
                  />
                  <div className="flex justify-end gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCommentForm(false);
                        setNewComment('');
                      }}
                      className="px-3 py-1.5 text-sm text-text-secondary hover:text-text transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmittingComment || !newComment.replace(/<[^>]*>/g, '').trim()}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-accent hover:bg-accent-dark disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
                    >
                      {isSubmittingComment ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                      Post Comment
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Comment List */}
            {topLevelComments.length === 0 ? (
              <p className="text-text-muted text-center py-4">No comments yet</p>
            ) : (
              <div className="space-y-4">
                {topLevelComments.map((comment) => {
                  const replies = getReplies(comment.id);
                  return (
                    <div key={comment.id} className="space-y-2">
                      {/* Parent Comment */}
                      <div className="p-4 bg-bg-tertiary rounded-xl border border-border">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center">
                              <User className="w-4 h-4 text-accent" />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{comment.userName}</p>
                              <p className="text-xs text-text-muted">
                                {formatDateTime(comment.createdAt)}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => startReply(comment)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-text-muted hover:text-accent hover:bg-accent/10 rounded transition-colors"
                          >
                            <Reply className="w-3 h-3" />
                            Reply
                          </button>
                        </div>
                        {comment.contentHtml ? (
                          <HtmlRenderer html={comment.contentHtml} />
                        ) : (
                          <p className="text-text-secondary whitespace-pre-wrap">
                            {comment.content}
                          </p>
                        )}
                      </div>

                      {/* Replies */}
                      {replies.length > 0 && (
                        <div className="ml-6 space-y-2 border-l-2 border-accent/20 pl-4">
                          {replies.map((reply) => (
                            <div
                              key={reply.id}
                              className="p-3 bg-bg-tertiary/50 rounded-lg border border-border/50"
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <div className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center">
                                    <User className="w-3 h-3 text-accent" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-xs">{reply.userName}</p>
                                    <p className="text-xs text-text-muted">
                                      {formatDateTime(reply.createdAt)}
                                    </p>
                                  </div>
                                </div>
                                <button
                                  onClick={() => startReply(comment)}
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs text-text-muted hover:text-accent hover:bg-accent/10 rounded transition-colors"
                                >
                                  <Reply className="w-2.5 h-2.5" />
                                </button>
                              </div>
                              {reply.contentHtml ? (
                                <div className="text-sm">
                                  <HtmlRenderer html={reply.contentHtml} />
                                </div>
                              ) : (
                                <p className="text-sm text-text-secondary whitespace-pre-wrap">
                                  {reply.content}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </motion.div>

        {/* Sidebar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-6"
        >
          {/* Metadata */}
          <div className="bg-bg-secondary rounded-xl border border-border p-4 space-y-4">
            <div>
              <p className="text-xs text-text-muted uppercase tracking-wider mb-1">Type</p>
              <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium ${type?.bg || 'bg-gray-500/10'} ${type?.color || 'text-gray-500'}`}>
                <TypeIcon className="w-3.5 h-3.5" />
                {type?.label || request.type}
              </div>
            </div>

            <div>
              <p className="text-xs text-text-muted uppercase tracking-wider mb-1">Created By</p>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-text-muted" />
                <span className="text-sm">{request.createdByName}</span>
              </div>
            </div>

            <div>
              <p className="text-xs text-text-muted uppercase tracking-wider mb-1">Created</p>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-text-muted" />
                <span className="text-sm">
                  {formatDate(request.createdAt)}
                </span>
              </div>
            </div>

            {request.approvedAt && (
              <div>
                <p className="text-xs text-text-muted uppercase tracking-wider mb-1">Approved</p>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-sm">
                    {formatDate(request.approvedAt)}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Tags */}
          <div className="bg-bg-secondary rounded-xl border border-border p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-text-muted uppercase tracking-wider">Tags</p>
              {unusedTags.length > 0 && (
                <div className="relative">
                  <button
                    onClick={() => setShowTagDropdown(!showTagDropdown)}
                    className="p-1 hover:bg-bg-tertiary rounded transition-colors"
                  >
                    <Plus className="w-4 h-4 text-text-muted" />
                  </button>
                  {showTagDropdown && (
                    <div className="absolute right-0 top-full mt-1 w-48 bg-bg-tertiary border border-border rounded-lg shadow-lg z-10 py-1">
                      {unusedTags.map((tag) => (
                        <button
                          key={tag.id}
                          onClick={() => handleAddTag(tag.id)}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-bg-secondary transition-colors flex items-center gap-2"
                        >
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: tag.color }}
                          />
                          {tag.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            {requestTags.length === 0 ? (
              <p className="text-sm text-text-muted">No tags</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {requestTags.map((tag) => (
                  <span
                    key={tag.id}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: `${tag.color}20`,
                      color: tag.color,
                    }}
                  >
                    {tag.name}
                    <button
                      onClick={() => handleRemoveTag(tag.id)}
                      className="hover:opacity-70 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
